import _classPrivateFieldGet from '@babel/runtime/helpers/classPrivateFieldGet';
import _classPrivateFieldSet from '@babel/runtime/helpers/classPrivateFieldSet';
import { testReducedMotion } from '@nrk/dh-basis-common';

function _classPrivateMethodInitSpec(obj, privateSet) { _checkPrivateRedeclaration(obj, privateSet); privateSet.add(obj); }
function _classPrivateFieldInitSpec(obj, privateMap, value) { _checkPrivateRedeclaration(obj, privateMap); privateMap.set(obj, value); }
function _checkPrivateRedeclaration(obj, privateCollection) { if (privateCollection.has(obj)) { throw new TypeError("Cannot initialize the same private elements twice on an object"); } }
function _classPrivateMethodGet(receiver, privateSet, fn) { if (!privateSet.has(receiver)) { throw new TypeError("attempted to get private field on non-instance"); } return fn; }

/*
 * Mixin for custom elements
 *
 * Keeps track of multiple scroll timelines and calls updatedCallback whenever
 * progress in a scroll timeline has changed.
 *
 * Inspired by https://drafts.csswg.org/scroll-animations-1/
 */
function ScrollanimasjonMixin(Base) {
  var _isWatching, _observer, _animation, _progress, _timeline, _createIntersectionObserver, _disconnectIntersectionObserver, _handleVisibilityChange, _startWatching, _stopWatching;
  return _isWatching = /*#__PURE__*/new WeakMap(), _observer = /*#__PURE__*/new WeakMap(), _animation = /*#__PURE__*/new WeakMap(), _progress = /*#__PURE__*/new WeakMap(), _timeline = /*#__PURE__*/new WeakMap(), _createIntersectionObserver = /*#__PURE__*/new WeakSet(), _disconnectIntersectionObserver = /*#__PURE__*/new WeakSet(), _handleVisibilityChange = /*#__PURE__*/new WeakMap(), _startWatching = /*#__PURE__*/new WeakSet(), _stopWatching = /*#__PURE__*/new WeakSet(), class extends Base {
    constructor() {
      super(...arguments);
      _classPrivateMethodInitSpec(this, _stopWatching);
      _classPrivateMethodInitSpec(this, _startWatching);
      _classPrivateMethodInitSpec(this, _disconnectIntersectionObserver);
      _classPrivateMethodInitSpec(this, _createIntersectionObserver);
      _classPrivateFieldInitSpec(this, _isWatching, {
        writable: true,
        value: false
      });
      _classPrivateFieldInitSpec(this, _observer, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec(this, _animation, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec(this, _progress, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec(this, _timeline, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec(this, _handleVisibilityChange, {
        writable: true,
        value: _ref => {
          var [containerIntersection] = _ref;
          if (containerIntersection.isIntersecting) {
            _classPrivateMethodGet(this, _startWatching, _startWatching2).call(this);
          } else {
            _classPrivateMethodGet(this, _stopWatching, _stopWatching2).call(this);
          }
        }
      });
    }
    connectedCallback() {
      var _super$connectedCallb;
      (_super$connectedCallb = super.connectedCallback) === null || _super$connectedCallb === void 0 || _super$connectedCallb.call(this);
      var userPrefersReducedMotion = testReducedMotion();
      if (this.reduceMotion.type === 'auto' && userPrefersReducedMotion) {
        this.progressUpdatedCallback(this.reduceMotion.defaultValue);
        return;
      }
      _classPrivateFieldSet(this, _timeline, new ViewTimeline({
        subject: this,
        axis: this.axis,
        inset: this.inset
      }));
      _classPrivateFieldSet(this, _animation, this.animate({}, {
        timeline: _classPrivateFieldGet(this, _timeline),
        rangeStart: this.rangeStart,
        rangeEnd: this.rangeEnd
      }));
      _classPrivateMethodGet(this, _createIntersectionObserver, _createIntersectionObserver2).call(this);
    }
    disconnectedCallback() {
      var _super$disconnectedCa;
      (_super$disconnectedCa = super.disconnectedCallback) === null || _super$disconnectedCa === void 0 || _super$disconnectedCa.call(this);
      _classPrivateMethodGet(this, _disconnectIntersectionObserver, _disconnectIntersectionObserver2).call(this);
      _classPrivateMethodGet(this, _stopWatching, _stopWatching2).call(this);
    }
    /* Override this method to perform updates */
    progressUpdatedCallback(progress) {
      console.log(progress.toFixed(3));
    }
    get axis() {
      var _this$getAttribute;
      return (_this$getAttribute = this.getAttribute('axis')) !== null && _this$getAttribute !== void 0 ? _this$getAttribute : 'block';
    }
    get inset() {
      var _this$getAttribute2;
      return (_this$getAttribute2 = this.getAttribute('inset')) !== null && _this$getAttribute2 !== void 0 ? _this$getAttribute2 : '0px 0px';
    }
    get rangeStart() {
      var _this$getAttribute3;
      return (_this$getAttribute3 = this.getAttribute('range-start')) !== null && _this$getAttribute3 !== void 0 ? _this$getAttribute3 : 'contain';
    }
    get rangeEnd() {
      var _this$getAttribute4;
      return (_this$getAttribute4 = this.getAttribute('range-end')) !== null && _this$getAttribute4 !== void 0 ? _this$getAttribute4 : 'contain';
    }
    get reduceMotion() {
      var attributeValue = this.getAttribute('reduce-motion') || 'auto 1';
      if (attributeValue === 'never') {
        return {
          type: 'never'
        };
      } else if (attributeValue.startsWith('auto')) {
        var [type, value = '1'] = attributeValue.trim().split(' ');
        try {
          var defaultValue = parseFloat(value);
          return {
            type,
            defaultValue: Math.min(Math.max(defaultValue, 0), 1)
          };
        } catch (_unused) {
          console.warn("reduceMotion must be either: \"none\" or \"auto N\" with N = number [0..1] number input is ".concat(value));
          return {
            type: 'auto',
            defaultValue: 1
          };
        }
      } else {
        console.warn("reduceMotion must be either: \"none\" or \"auto N\" with N = number [0..1] type input is(".concat(attributeValue, ")"));
        return {
          type: 'auto',
          defaultValue: 1
        };
      }
    }
    get timeline() {
      return _classPrivateFieldGet(this, _timeline);
    }
  };
  function _createIntersectionObserver2() {
    // eslint-disable-next-line compat/compat
    _classPrivateFieldSet(this, _observer, new IntersectionObserver(_classPrivateFieldGet(this, _handleVisibilityChange), {
      /*
      Use document as root, so that intersections are tracked against iframe scrolling viewport if embedded (polopoly-preview).
      (No root would use the viewport, even when embedded in iframe)
      https://w3c.github.io/IntersectionObserver/#dom-intersectionobserver-root
       Currently disabled due to a bug in Chrome: https://issues.chromium.org/issues/40895558
      Todo: enable once chrome bug is fixed
      root: document,
       */
      threshold: [0]
    }));
    _classPrivateFieldGet(this, _observer).observe(this);
  }
  function _disconnectIntersectionObserver2() {
    if (_classPrivateFieldGet(this, _observer)) {
      _classPrivateFieldGet(this, _observer).disconnect();
    }
  }
  function _startWatching2() {
    if (_classPrivateFieldGet(this, _isWatching)) {
      return;
    }
    var loop = () => {
      if (_classPrivateFieldGet(this, _isWatching)) {
        requestAnimationFrame(loop);
      } else {
        return;
      }
      var progress;
      var currentTime = _classPrivateFieldGet(this, _animation).currentTime;
      if (currentTime.unit === 'percent') {
        // https://github.com/w3c/csswg-drafts/issues/8799
        var rangeProgress = _classPrivateFieldGet(this, _animation).currentTime.value / _classPrivateFieldGet(this, _animation).effect.getComputedTiming().endTime.value;
        progress = Math.max(0, Math.min(rangeProgress, 1));
      }
      if (_classPrivateFieldGet(this, _progress) !== progress) {
        this.progressUpdatedCallback(progress);
        _classPrivateFieldSet(this, _progress, progress);
      }
    };
    _classPrivateFieldSet(this, _isWatching, true);
    // eslint-disable-next-line compat/compat
    requestAnimationFrame(loop);
  }
  function _stopWatching2() {
    _classPrivateFieldSet(this, _isWatching, false);
  }
}

class Scrollprogress extends ScrollanimasjonMixin(HTMLElement) {
  progressUpdatedCallback(progress) {
    var _this$targetElement;
    (_this$targetElement = this.targetElement) === null || _this$targetElement === void 0 || _this$targetElement.setAttribute('data-progress', progress.toFixed(4));
  }
  get targetElement() {
    return this.querySelector('[data-progress]');
  }
}

export { ScrollanimasjonMixin, Scrollprogress };
//# sourceMappingURL=index.esm.js.map
